import React from 'react'
import { Box } from '@mui/material'

import Txt from './Txt'
import Button from './Button'
import IconButton from './IconButton'
import CheckBox from './CheckBox'
import Dialog, { DialogButtonRow } from './Dialog'
import { handleMutationError } from '../utils/errors'
import { DEVELOPMENT, GESCHAEFTSFUEHRER, TEST_ADDRESS } from '../consts'
import {
	RestaurantCommunication,
	Settlement,
	SettlementRestaurant,
	useRestaurantCommunications,
} from '../utils/settlement'
import createRestaurantMessage, { RestaurantMessage } from '../mutations/createRestaurantMessage'
import sendMessage, { RestaurantMessageSendResult } from '../mutations/sendMessage'

type PrintFormat = 'PAPER' | 'POSTCARD'

// Brief und Karte sprechen den Empfänger auf seinen Mindestbestellwert ab 50 € an.
const MIN_ORDER_VALUE_THRESHOLD = 50

// Fast fensterfüllend, damit sich der Brief in der Vorschau lesen lässt wie ausgedruckt.
const DIALOG_WIDTH = '95vw'

// Links die Vorschau, rechts die Restaurants; die Liste scrollt für sich, wenn sie länger ist.
const sxColumns = { display: 'flex', gap: '1.5rem', alignItems: 'stretch' } as const

const sxRecipients = { flex: '0 0 22rem', height: '80vh', overflowY: 'auto' } as const

// Beide Seiten untereinander im Viewer des Browsers, hoch genug für eine ganze A4-Seite.
const sxPreview = {
	display: 'block',
	flex: '1',
	minWidth: '0',
	height: '80vh',
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '0.5rem',
	bgcolor: 'grey.100',
} as const

const sxPostcardPreview = { flex: '1', minWidth: '0', height: '80vh', overflowY: 'auto' } as const

// Seitenverhältnis der Karte (1795 × 1287 px), damit der Platz beim Laden nicht springt.
const sxImage = {
	display: 'block',
	width: '100%',
	aspectRatio: '1795 / 1287',
	borderRadius: '0.5rem',
	boxShadow: 2,
	bgcolor: 'grey.100',
} as const

const sxBack = {
	whiteSpace: 'pre-line',
	border: '1px solid',
	borderColor: 'divider',
	borderRadius: '0.5rem',
	p: '0.75rem 1rem',
	m: '1rem 0 0 0',
} as const

// Links der Text, rechts die Anschrift, wie EchtPost die Rückseite aufteilt.
const sxBackColumns = { display: 'flex', gap: '1.5rem', m: '0.25rem 0 0 0' } as const

const sxAddress = {
	flex: '0 0 12rem',
	alignSelf: 'flex-end',
	pl: '1rem',
	borderLeft: '1px solid',
	borderColor: 'divider',
} as const

const sxRow = {
	display: 'flex',
	gap: '0.5rem',
	alignItems: 'center',
	p: '0.25rem 0',
	borderBottom: '1px solid',
	borderColor: 'divider',
} as const

const sxUnprofitableRow = { ...sxRow, opacity: 0.4 } as const

// Der eigene Eintrag steht wie ein Restaurant in der Liste: ankreuzen, Vorschau, senden. Sein
// Brief bzw. seine Karte trägt den Firmennamen statt eines Restaurants und geht an die Testadresse.
const SELF_ID = 'self'

const SELF_RECIPIENT: SettlementRestaurant = {
	id: SELF_ID,
	name: 'Beyer Development',
	addressLine: TEST_ADDRESS.street,
	addressZipCode: TEST_ADDRESS.postalCode,
	addressCity: TEST_ADDRESS.city,
	phone: null,
	email: null,
	relevanceScore: null,
	isChain: false,
	driveDistanceKm: null,
	driveMinutes: null,
	minOrderValue: null,
	deliveryFee: null,
	rating: null,
	ratingCount: null,
	cuisines: null,
	cuisineCategory: null,
	isInDeliveryArea: false,
	slug: SELF_ID,
	lat: null,
	lon: null,
	potentialBySize: [],
	singleOrderMarginShare: null,
}

const RecipientRow = React.memo(
	({
		provider,
		sentLetter,
		checked,
		previewed,
		onToggle,
		onPreview,
	}: {
		provider: SettlementRestaurant
		sentLetter: RestaurantCommunication | undefined
		checked: boolean
		previewed: boolean
		onToggle: (nextValue: boolean, id?: string) => void
		onPreview: (slug: string) => void
	}): JSX.Element => {
		const handlePreview = React.useCallback(
			() => onPreview(provider.slug),
			[onPreview, provider.slug]
		)
		// Wer schon eine Einzelfahrt zum MBW mit Verlust fährt, nutzt Lieferando nur zur Sichtbarkeit;
		// ihn anzuschreiben lohnt nicht.
		const unprofitable =
			provider.singleOrderMarginShare !== null && provider.singleOrderMarginShare < 0

		return (
			<Box sx={unprofitable ? sxUnprofitableRow : sxRow}>
				<Box flex="1" minWidth="0">
					<CheckBox
						id={provider.slug}
						label={provider.name}
						defaultValue={checked}
						disabled={provider.addressLine === null || unprofitable}
						onChange={onToggle}
					/>
					<Txt variant="caption" color="text.secondary" noWrap>
						{provider.addressLine === null
							? 'keine Anschrift'
							: [provider.addressLine, provider.addressCity].filter(Boolean).join(', ')}
					</Txt>
					{sentLetter ? (
						<Txt variant="caption" color="success.main" fontWeight="bold" noWrap>
							{`Brief verschickt am ${new Date(sentLetter.createdAt).toLocaleDateString(
								'de-DE'
							)} · Auftrag ${sentLetter.reference}`}
						</Txt>
					) : null}
				</Box>
				<Txt
					variant="caption"
					color="text.secondary"
					width="3rem"
					textAlign="right"
					title="Deckungsbeitrag einer einzelnen, selbst ausgefahrenen Lieferando-Bestellung zum MBW"
				>
					{provider.singleOrderMarginShare === null
						? ''
						: `${Math.round(provider.singleOrderMarginShare * 100)} %`}
				</Txt>
				<Txt variant="body2" width="3.5rem" textAlign="right">
					{provider.minOrderValue === null ? '' : `${provider.minOrderValue} €`}
				</Txt>
				{/* Offenes Auge: dieses Stück steht in der Vorschau. */}
				<IconButton
					iconName={previewed ? 'Visibility' : 'VisibilityOff'}
					iconColor={previewed ? 'primary.main' : 'text.disabled'}
					toolTip={previewed ? 'In der Vorschau' : 'Vorschau anzeigen'}
					onClick={handlePreview}
					disabled={unprofitable}
				/>
			</Box>
		)
	}
)
RecipientRow.displayName = 'RecipientRow'

const ResultRow = React.memo(
	({
		result,
		format,
	}: {
		result: RestaurantMessageSendResult
		format: PrintFormat
	}): JSX.Element => (
		<Box sx={sxRow}>
			<Box flex="1" minWidth="0">
				<Txt variant="body2" noWrap>
					{result.restaurantName}
				</Txt>
				{result.error === null ? (
					<Txt variant="caption" color="text.secondary">
						{format === 'PAPER'
							? `an LetterXpress übergeben · Auftrag ${result.reference}`
							: `an EchtPost übergeben · ID ${result.reference}`}
					</Txt>
				) : (
					<Txt variant="caption" color="error.main">
						{result.error}
					</Txt>
				)}
			</Box>
		</Box>
	)
)
ResultRow.displayName = 'ResultRow'

export const toPdfUrl = (pdf: string): string =>
	URL.createObjectURL(
		new Blob([Uint8Array.from(atob(pdf), (character) => character.charCodeAt(0))], {
			type: 'application/pdf',
		})
	)

export const LetterPreview = React.memo(({ pdf }: { pdf: string | undefined }): JSX.Element => {
	const previewUrl = React.useMemo(() => (pdf === undefined ? undefined : toPdfUrl(pdf)), [pdf])

	React.useEffect(
		() => () => {
			if (previewUrl !== undefined) URL.revokeObjectURL(previewUrl)
		},
		[previewUrl]
	)

	return previewUrl === undefined ? (
		<Box sx={sxPreview}>
			<Txt variant="body2" color="text.secondary" m="1rem">
				Vorschau wird erzeugt …
			</Txt>
		</Box>
	) : (
		<Box component="iframe" sx={sxPreview} src={previewUrl} title="Vorschau des Briefs" />
	)
})
LetterPreview.displayName = 'LetterPreview'

// Vorderseite als Bild, Rückseite mit dem Text und der Anschrift, wie sie an EchtPost gehen: an
// die eigene Adresse mit dem Namen des Geschäftsführers, an ein Restaurant mit dessen Namen.
const PostcardPreview = React.memo(
	({
		material,
		recipient,
	}: {
		material: RestaurantMessage | undefined
		recipient: SettlementRestaurant | undefined
	}): JSX.Element => (
		<Box sx={sxPostcardPreview}>
			<Box
				component="img"
				sx={sxImage}
				src={material?.png ? `data:image/png;base64,${material.png}` : undefined}
			/>
			<Txt variant="caption" color="text.secondary">
				{material ? '' : 'Vorschau wird erzeugt … '}Vorderseite
			</Txt>
			<Box sx={sxBack}>
				<Txt variant="caption" color="text.secondary">
					Rückseite (Layout, Adresse und Briefmarke setzt EchtPost)
				</Txt>
				<Box sx={sxBackColumns}>
					<Box flex="1" minWidth="0">
						{material?.backText ? (
							<Txt variant="body2" lineBreaks>
								{material.backText}
							</Txt>
						) : null}
					</Box>
					<Box sx={sxAddress}>
						<Txt variant="body2" lineBreaks>
							{recipient?.addressLine
								? [
										recipient.slug === SELF_ID ? GESCHAEFTSFUEHRER : recipient.name,
										recipient.addressLine,
										[recipient.addressZipCode, recipient.addressCity].filter(Boolean).join(' '),
								  ].join('\n')
								: 'Keine vollständige Anschrift'}
						</Txt>
					</Box>
				</Box>
			</Box>
		</Box>
	)
)
PostcardPreview.displayName = 'PostcardPreview'

const SettlementMessageDialog = ({
	settlement,
	format,
	dismissPortal,
}: {
	settlement: Settlement
	format: PrintFormat
	dismissPortal: () => void
}): JSX.Element => {
	// Die Karte nennt den Mindestbestellwert fest; nur Briefe dürfen auch günstigere Restaurants anschreiben.
	const [showCheapProviders, setShowCheapProviders] = React.useState(false)
	const recipients = React.useMemo(
		() => [
			SELF_RECIPIENT,
			...(settlement.restaurants ?? [])
				.filter(
					(provider) =>
						showCheapProviders ||
						(provider.minOrderValue !== null && provider.minOrderValue >= MIN_ORDER_VALUE_THRESHOLD)
				)
				// Nach Deckungsbeitrag absteigend; ohne Fahrzeit unbekannt und ans Ende.
				.sort(
					(a, b) =>
						(b.singleOrderMarginShare ?? -Infinity) - (a.singleOrderMarginShare ?? -Infinity) ||
						(b.minOrderValue ?? 0) - (a.minOrderValue ?? 0)
				),
		],
		[settlement.restaurants, showCheapProviders]
	)

	// Je Restaurant der jüngste echt verschickte Brief, über alle Orte, damit niemand zweimal
	// angeschrieben wird. Das Log kommt neueste zuerst, also gewinnt der erste je Slug.
	const { communications } = useRestaurantCommunications()
	const latestSentLetters = React.useMemo(
		() =>
			communications
				.filter(({ channel }) => channel === 'letter')
				.reduceRight<Record<string, RestaurantCommunication>>(
					(bySlug, letter) => ({ ...bySlug, [letter.slug]: letter }),
					{}
				),
		[communications]
	)

	// Vorab das erste wirtschaftliche Restaurant, sonst das eigene Teststück.
	const [previewId, setPreviewId] = React.useState<string>(
		(
			recipients
				.slice(1)
				.find(({ singleOrderMarginShare }) => (singleOrderMarginShare ?? 0) >= 0) ?? SELF_RECIPIENT
		).slug
	)
	// Brief bzw. Karte je Restaurant: wer hin und her schaltet, wartet nur einmal.
	const [materials, setMaterials] = React.useState<Record<string, RestaurantMessage>>({})
	const [checkedIds, setCheckedIds] = React.useState<string[]>([])
	// Ein ausgeblendetes Restaurant bleibt nicht unsichtbar ausgewählt.
	const selectedIds = React.useMemo(
		() => checkedIds.filter((checkedId) => recipients.some(({ slug }) => slug === checkedId)),
		[checkedIds, recipients]
	)
	const [sandbox, setSandbox] = React.useState(DEVELOPMENT)
	const [sending, setSending] = React.useState(false)
	const [results, setResults] = React.useState<{
		items: RestaurantMessageSendResult[]
		sandbox: boolean
	} | null>(null)

	React.useEffect(() => {
		const loadMaterial = async (recipientId: string) => {
			const result = await createRestaurantMessage({
				settlementIds: [settlement.id],
				slug: recipientId === SELF_ID ? undefined : recipientId,
				restaurantName: recipientId === SELF_ID ? SELF_RECIPIENT.name : undefined,
				format,
			})
			if (result.success) {
				setMaterials((previousMaterials) => ({
					...previousMaterials,
					[recipientId]: result.message,
				}))
			} else {
				handleMutationError('createRestaurantMessage', result.errors)
			}
		}
		if (materials[previewId] === undefined) loadMaterial(previewId)
	}, [format, materials, previewId, settlement.id])

	const previewRecipient = recipients.find(({ slug }) => slug === previewId)

	const handleToggle = React.useCallback((nextValue: boolean, id?: string) => {
		setCheckedIds((previousIds) => [
			...previousIds.filter((checkedId) => checkedId !== id),
			...(nextValue && id !== undefined ? [id] : []),
		])
	}, [])

	const handleDownload = React.useCallback(() => {
		const pdf = materials[previewId]?.pdf
		if (pdf && previewRecipient) {
			const url = toPdfUrl(pdf)
			const link = document.createElement('a')
			link.href = url
			link.download = `Mitbestellt-Brief-${previewRecipient.name}-${settlement.name}.pdf`
			link.click()
			URL.revokeObjectURL(url)
		}
	}, [materials, previewId, previewRecipient, settlement.name])

	// Nach dem Versand steht rechts das Ergebnis. Aus der Sandbox geht es zurück zur Auswahl, nach
	// einem echten Versand nicht: ein Klick, ein gedrucktes Stück.
	const handleBackToSelection = React.useCallback(() => setResults(null), [])

	const handleSend = React.useCallback(async () => {
		setSending(true)
		const result = await sendMessage({
			settlementIds: [settlement.id],
			slugs: selectedIds.map((recipientId) => (recipientId === SELF_ID ? null : recipientId)),
			sandbox,
			format,
		})
		setSending(false)
		if (result.success) {
			setResults({ items: result.results, sandbox })
		} else {
			handleMutationError('sendMessage', result.errors)
		}
	}, [format, sandbox, selectedIds, settlement.id])

	return (
		<Box p="1.5rem" width={DIALOG_WIDTH} maxWidth="100%">
			<Txt fontWeight="bold" variant="h4" m="0 0 1rem 0">
				{format === 'PAPER' ? 'Brief' : 'Postkarte'} {settlement.name}
			</Txt>
			<Box sx={sxColumns}>
				{format === 'PAPER' ? (
					<LetterPreview pdf={materials[previewId]?.pdf ?? undefined} />
				) : (
					<PostcardPreview material={materials[previewId]} recipient={previewRecipient} />
				)}
				<Box sx={sxRecipients}>
					<Txt variant="subtitle2" m="0 0 0.25rem 0">
						{results
							? `${results.sandbox ? 'Sandbox: ' : ''}Versand`
							: showCheapProviders
							? 'Empfänger (alle Restaurants)'
							: `Empfänger (Restaurants ab ${MIN_ORDER_VALUE_THRESHOLD} € Mindestbestellwert)`}
					</Txt>
					{results || format !== 'PAPER' ? null : (
						<CheckBox
							label={`Auch Restaurants unter ${MIN_ORDER_VALUE_THRESHOLD} € Mindestbestellwert`}
							defaultValue={showCheapProviders}
							onChange={setShowCheapProviders}
							small
						/>
					)}
					{results
						? results.items.map((result) => (
								<ResultRow key={result.slug ?? SELF_ID} result={result} format={format} />
						  ))
						: recipients.map((provider) => (
								<RecipientRow
									key={provider.slug}
									provider={provider}
									sentLetter={latestSentLetters[provider.slug]}
									checked={selectedIds.includes(provider.slug)}
									previewed={provider.slug === previewId}
									onToggle={handleToggle}
									onPreview={setPreviewId}
								/>
						  ))}
				</Box>
			</Box>
			<DialogButtonRow>
				{/* Briefe schickt die API lokal ohnehin nur im Testmodus; der Haken zeigt das ehrlich an. */}
				<CheckBox
					label={
						format === 'POSTCARD'
							? 'Sandbox (nichts wird gedruckt)'
							: DEVELOPMENT
							? 'Sandbox (lokal immer, nichts wird gedruckt)'
							: 'Sandbox (nur LetterXpress-Postbox, nichts wird gedruckt)'
					}
					defaultValue={sandbox}
					disabled={format === 'PAPER' && DEVELOPMENT}
					onChange={setSandbox}
				/>
				{format === 'PAPER' ? (
					<Button m="0 0 0 1rem" onClick={handleDownload} disabled={!materials[previewId]?.pdf}>
						PDF herunterladen
					</Button>
				) : null}
				{results ? (
					results.sandbox ? (
						<Button variant="mainButton" m="0 0 0 1rem" onClick={handleBackToSelection}>
							Zurück zur Auswahl
						</Button>
					) : null
				) : (
					<Button
						variant="mainButton"
						m="0 0 0 1rem"
						onClick={handleSend}
						loading={sending}
						disabled={!selectedIds.length}
					>
						{`An ${selectedIds.length} Empfänger senden`}
					</Button>
				)}
				<Button m="0 0 0 1rem" onClick={dismissPortal}>
					Schließen
				</Button>
			</DialogButtonRow>
		</Box>
	)
}

// Vorschau und Versand der Briefe (A4, LetterXpress) oder Postkarten (EchtPost) eines Orts, ein
// eigenes Stück je Restaurant.
export const renderSettlementMessageDialog = (settlement: Settlement, format: PrintFormat): void =>
	Dialog.render({
		width: DIALOG_WIDTH,
		renderContent: ({ dismissPortal }) => (
			<SettlementMessageDialog
				settlement={settlement}
				format={format}
				dismissPortal={dismissPortal}
			/>
		),
	})
